import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'

import AppBar from '@mui/material/AppBar'
import Avatar from '@mui/material/Avatar'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Container from '@mui/material/Container'
import Divider from '@mui/material/Divider'
import IconButton from '@mui/material/IconButton'
import ListItemIcon from '@mui/material/ListItemIcon'
import ListItemText from '@mui/material/ListItemText'
import Menu from '@mui/material/Menu'
import MenuItem from '@mui/material/MenuItem'
import Toolbar from '@mui/material/Toolbar'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'

import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings'
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents'
import LoginOutlinedIcon from '@mui/icons-material/LoginOutlined'
import LogoutIcon from '@mui/icons-material/Logout'
import MenuIcon from '@mui/icons-material/Menu'
import PeopleIcon from '@mui/icons-material/People'
import PersonIcon from '@mui/icons-material/Person'
import SportsIcon from '@mui/icons-material/Sports'
import SportsBasketballIcon from '@mui/icons-material/SportsBasketball'

import { useAppSelector } from '../../redux/hooks'
import { getAuthService } from '../../services/authService'

export const Navbar = () => {

    const [anchorElNav, setAnchorElNav] = useState<null | HTMLElement>(null)
    const [anchorElUser, setAnchorElUser] = useState<null | HTMLElement>(null)
    const currentUser = useAppSelector(state => state.currentUser)
    const auth = useAppSelector(state => state.auth)
    const isSuperAdmin = auth.accessTokenPayload ? auth.accessTokenPayload['cognito:groups']?.includes('superadmin') : false
    const navigate = useNavigate()

    const links: { name: string, path: string, icon: JSX.Element }[] = [
        { name: 'Teams', path: '/teams', icon: <SportsBasketballIcon fontSize="small" /> },
        { name: 'Matches', path: '/matches', icon: <SportsIcon fontSize="small" /> },
        { name: 'Players', path: '/players', icon: <PeopleIcon fontSize="small" /> },
        // { name: 'Arch Madness Brackets', path: '/brackets' },
        { name: 'Leagues', path: '/leagues', icon: <EmojiEventsIcon fontSize="small" /> },
    ]

    const handleOpenNavMenu = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorElNav(event.currentTarget)
    }

    const handleOpenUserMenu = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorElUser(event.currentTarget)
    }

    const handleCloseNavMenu = () => {
        setAnchorElNav(null)
    }

    const handleCloseUserMenu = () => {
        setAnchorElUser(null)
    }

    const handleLogin = () => {
        navigate('/auth/sign-in')
    }

    const signOut = () => {
        const authService = getAuthService()
        authService.logout()
    }

    return (
        <AppBar position="static">
            <Container>
                <Toolbar disableGutters>
                    <Link to='/' style={{ textDecoration: 'none', color: 'white' }}>
                        <Typography
                            variant="h6"
                            noWrap
                            component="p"
                            sx={{
                                mr: 2,
                                display: { xs: 'none', md: 'flex' },
                                color: 'inherit',
                                textDecoration: 'none',
                                fontWeight: 600,
                            }}
                        >
                            MVC Games
                        </Typography>
                    </Link>

                    <Box sx={{ flexGrow: 1, display: { xs: 'flex', md: 'none' } }}>
                        <IconButton
                            size="large"
                            aria-label="navigation menu"
                            aria-controls="menu-appbar"
                            aria-haspopup="true"
                            onClick={handleOpenNavMenu}
                            color="inherit"
                            sx={{ mr: 1 }}
                        >
                            <MenuIcon />
                        </IconButton>
                        <Menu
                            id="menu-appbar"
                            anchorEl={anchorElNav}
                            anchorOrigin={{
                                vertical: 'bottom',
                                horizontal: 'left',
                            }}
                            keepMounted
                            transformOrigin={{
                                vertical: 'top',
                                horizontal: 'left',
                            }}
                            open={Boolean(anchorElNav)}
                            onClose={handleCloseNavMenu}
                            sx={{
                                display: { xs: 'block', md: 'none' },
                            }}
                            PaperProps={{
                                sx: {
                                    minWidth: 200,
                                    mt: 1,
                                }
                            }}
                        >
                            {
                                links.map((link) => (
                                    <Link key={link.path} to={link.path} style={{ textDecoration: 'none', color: 'inherit' }}>
                                        <MenuItem
                                            onClick={handleCloseNavMenu}
                                            sx={{ py: 1.5, px: 2 }}
                                        >
                                            <ListItemIcon sx={{ minWidth: 36 }}>
                                                {link.icon}
                                            </ListItemIcon>
                                            <ListItemText
                                                primary={link.name}
                                                primaryTypographyProps={{
                                                    fontSize: 15,
                                                    fontWeight: 500
                                                }}
                                            />
                                        </MenuItem>
                                    </Link>
                                ))
                            }
                            {currentUser.account && [
                                <Divider key="divider-1" sx={{ my: 1 }} />,
                                <MenuItem
                                    key="profile"
                                    onClick={() => {
                                        handleCloseNavMenu()
                                        navigate('/my-account')
                                    }}
                                    sx={{ py: 1.5, px: 2 }}
                                >
                                    <ListItemIcon sx={{ minWidth: 36 }}>
                                        <PersonIcon fontSize="small" />
                                    </ListItemIcon>
                                    <ListItemText
                                        primary="Profile"
                                        primaryTypographyProps={{
                                            fontSize: 15,
                                            fontWeight: 500
                                        }}
                                    />
                                </MenuItem>,
                                ...(isSuperAdmin ? [
                                    <MenuItem
                                        key="admin"
                                        onClick={() => {
                                            handleCloseNavMenu()
                                            navigate('/admin')
                                        }}
                                        sx={{ py: 1.5, px: 2 }}
                                    >
                                        <ListItemIcon sx={{ minWidth: 36 }}>
                                            <AdminPanelSettingsIcon fontSize="small" />
                                        </ListItemIcon>
                                        <ListItemText
                                            primary="Admin Panel"
                                            primaryTypographyProps={{
                                                fontSize: 15,
                                                fontWeight: 500
                                            }}
                                        />
                                    </MenuItem>
                                ] : []),
                                <MenuItem
                                    key="signout"
                                    onClick={() => {
                                        handleCloseNavMenu()
                                        signOut()
                                    }}
                                    sx={{ py: 1.5, px: 2 }}
                                >
                                    <ListItemIcon sx={{ minWidth: 36 }}>
                                        <LogoutIcon fontSize="small" />
                                    </ListItemIcon>
                                    <ListItemText
                                        primary="Sign Out"
                                        primaryTypographyProps={{
                                            fontSize: 15,
                                            fontWeight: 500
                                        }}
                                    />
                                </MenuItem>
                            ]}
                            {!currentUser.account && [
                                <Divider key="divider-2" sx={{ my: 1 }} />,
                                <MenuItem
                                    key="signin"
                                    onClick={() => {
                                        handleCloseNavMenu()
                                        handleLogin()
                                    }}
                                    sx={{ py: 1.5, px: 2 }}
                                >
                                    <ListItemIcon sx={{ minWidth: 36 }}>
                                        <LoginOutlinedIcon fontSize="small" />
                                    </ListItemIcon>
                                    <ListItemText
                                        primary="Sign In"
                                        primaryTypographyProps={{
                                            fontSize: 15,
                                            fontWeight: 500
                                        }}
                                    />
                                </MenuItem>
                            ]}
                        </Menu>
                    </Box>
                    <Box sx={{
                        position: 'absolute',
                        left: '50%',
                        transform: 'translateX(-50%)',
                        display: { xs: 'flex', md: 'none' }
                    }}>
                        <Link to='/' style={{ textDecoration: 'none', color: 'white' }}>
                            <Typography
                                variant="h6"
                                noWrap
                                component='p'
                                sx={{
                                    fontWeight: 700,
                                    letterSpacing: '.5px',
                                    color: 'inherit',
                                    textDecoration: 'none',
                                }}
                            >
                                MVC Games
                            </Typography>
                        </Link>
                    </Box>
                    <Box sx={{ flexGrow: 1, display: { xs: 'none', md: 'flex' } }}>
                        {
                            links.map(link => (
                                <Link key={link.path} to={link.path} style={{ textDecoration: 'none' }}>
                                    <Button
                                        onClick={handleCloseNavMenu}
                                        sx={{ my: 2, color: 'white', display: 'block' }}
                                    >
                                        {link.name}
                                    </Button>
                                </Link>
                            ))
                        }
                    </Box>
                    <Box sx={{ flexGrow: 0, display: { xs: 'none', md: 'block' } }}>
                        {
                            currentUser.account ?
                                <Tooltip title="Open settings">
                                    <IconButton onClick={handleOpenUserMenu} sx={{ p: 0 }}>
                                        <Avatar alt={currentUser.account.username}>{currentUser.account.username.charAt(0)}</Avatar>
                                    </IconButton>
                                </Tooltip> :
                                <IconButton onClick={handleLogin} sx={{ p: 0, color: 'white' }}>
                                    <LoginOutlinedIcon />
                                </IconButton>
                        }
                        <Menu
                            sx={{ mt: '45px' }}
                            id="menu-appbar"
                            anchorEl={anchorElUser}
                            anchorOrigin={{
                                vertical: 'top',
                                horizontal: 'right',
                            }}
                            keepMounted
                            transformOrigin={{
                                vertical: 'top',
                                horizontal: 'right',
                            }}
                            open={Boolean(anchorElUser)}
                            onClose={handleCloseUserMenu}
                        >
                            <MenuItem onClick={handleCloseUserMenu}>
                                <Typography sx={{ textAlign: 'center' }} onClick={() => navigate('/my-account')}>Profile</Typography>
                            </MenuItem>
                            {
                                isSuperAdmin &&
                                <MenuItem onClick={handleCloseUserMenu}>
                                    <Typography sx={{ textAlign: 'center' }} onClick={() => navigate('/admin')}>Admin Panel</Typography>
                                </MenuItem>
                            }
                            <MenuItem onClick={handleCloseUserMenu}>
                                <Typography sx={{ textAlign: 'center' }} onClick={signOut}>Sign Out</Typography>
                            </MenuItem>
                        </Menu>
                    </Box>
                </Toolbar>
            </Container>
        </AppBar>
    )
}